/**
 * Home's moving parts. design-handoff/screens/home.md, specs/home-spec.md (Shopping v13 1j,
 * Tablet v1, Desktop v2/v3).
 *
 * - <cw-home-hero>: the rotating line under "Freshly" (every 2.4 s, the prototype's rot();
 *   still when reduced motion is on), and the due shortcut for a returning customer:
 *   "Likely running low · n items" + ADD, which puts their due usuals in the basket
 *   (assets/cw-usuals.js) and turns into "Due items added ✓ UNDO", with a toast that can
 *   undo it too. Nothing due = no card.
 * - <cw-home-delivery>: "Do we deliver to you?" for the chosen address, from Flow's answer
 *   (assets/cw-delivery.js). A returning customer whose address we deliver to does not see it.
 * - <cw-home-favourites>: the tablet's ‹ › arrows (one view at a time, as Tablet v1).
 * - Phones: scrolling down past 60 px folds the header row and slides the tabs away; any
 *   scroll up brings both back; the slot strip always stays (home.md "Scroll"). The header is
 *   fixed there, so folding it never moves the page.
 * - Buttons in the page that ask for a sheet or panel (ORDER AGAIN, the address button)
 *   announce 'cw:<name>:open', as the header does; whatever is built claims it.
 */
import { view, chosen } from '@cw/delivery';
import { dueItems, namesOf, addDue } from '@cw/usuals';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const PHONE = window.matchMedia('(max-width: 599.98px)');
const FALLBACK = { orderagain: () => '/account' };

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "800 m", "3.9 km" (as the basket says it). */
function distance(km) {
  const k = Number(km);
  if (!Number.isFinite(k)) return '';
  return k < 1 ? `${Math.round(k * 1000)} m` : `${Math.round(k * 10) / 10} km`;
}

class CwHomeHero extends HTMLElement {
  #timer = 0;
  #done = false;

  connectedCallback() {
    this.#rotate();
    this.due = this.querySelector('[data-cw="due"]');
    this.doneStrip = this.querySelector('[data-cw="due-done"]');
    if (!this.due) return;
    this.querySelector('[data-cw="due-add"]').addEventListener('click', this.#add);
    this.querySelector('[data-cw="due-undo"]').addEventListener('click', () => this.undo?.());
    document.addEventListener('cw:cart:change', this.#paint);
    this.#paint();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    document.removeEventListener('cw:cart:change', this.#paint);
  }

  #rotate() {
    const words = (this.dataset.words || '').split(',').map((w) => w.trim()).filter(Boolean);
    if (words.length < 2 || REDUCED.matches) return;
    let i = 0;
    this.#timer = setInterval(() => {
      if (document.hidden) return;
      const current = this.querySelector('.cw-home-hero__rw');
      if (!current) return;
      i = (i + 1) % words.length;
      const next = current.cloneNode();
      next.textContent = words[i];
      current.replaceWith(next); // a new element restarts the entrance
    }, 2400);
  }

  #paint = () => {
    if (this.#done) return;
    const items = dueItems();
    this.items = items;
    this.due.hidden = items.length === 0;
    if (!items.length) return;
    this.querySelector('[data-cw="due-head"]').textContent = `Likely running low · ${plural(items.length, 'item')}`;
    this.querySelector('[data-cw="due-names"]').textContent = namesOf(items);
  };

  // The card turns at once (the basket follows, cw-cart.js); UNDO waits for the add to land.
  #add = (event) => {
    const button = event.currentTarget;
    const items = this.items || [];
    if (!items.length) return;
    // Offline, nothing is added and the offline strip says why (toast-offline.md).
    if (navigator.onLine === false) {
      document.dispatchEvent(new CustomEvent('cw:offline'));
      return;
    }
    this.#done = true;
    const run = addDue(items, button);
    this.due.hidden = true;
    this.doneStrip.hidden = false;
    this.querySelector('[data-cw="due-undo"]')?.focus({ preventScroll: true });
    this.undo = async () => {
      this.undo = null;
      document.dispatchEvent(new CustomEvent('cw:toast', { detail: { hide: true } }));
      await (await run)();
      this.doneStrip.hidden = true;
      this.#done = false;
      this.#paint();
    };
    document.dispatchEvent(
      new CustomEvent('cw:toast', { detail: { text: `${plural(items.length, 'due item')} added`, undo: () => this.undo?.() } })
    );
  };
}

class CwHomeDelivery extends HTMLElement {
  connectedCallback() {
    document.addEventListener('cw:delivery:change', this.#paint);
    this.#paint({ detail: view() });
  }

  disconnectedCallback() {
    document.removeEventListener('cw:delivery:change', this.#paint);
  }

  #paint = (event) => {
    const v = event?.detail || view();
    const here = chosen();
    const set = (name, text) => {
      const el = this.querySelector(`[data-cw="${name}"]`);
      if (el) el.textContent = text;
    };
    if (!here) {
      set('addr-name', 'Set location');
      set('addr-line', 'Area, apartment or pincode');
      set('addr-go', 'SET');
    } else {
      const words = String(here.address || here.label || '').trim();
      set('addr-name', v.label || String(here.tag || here.label || words).split(',')[0]);
      set('addr-line', words);
      set('addr-go', 'CHANGE');
    }

    const box = this.querySelector('[data-cw="result"]');
    const a = here && v.known ? v.answer : null;
    if (box) {
      box.hidden = !a;
      if (a && !a.serviceable) {
        box.dataset.tier = 'far';
        set('result-head', 'Outside our delivery area.');
        set('result-text', `${distance(a.distance_km)} away. Order online and pick up at our shop.`);
      } else if (a) {
        const s = a.standard || {};
        const tomorrow = /^tomorrow/i.test(s.when || '');
        box.dataset.tier = tomorrow ? 'mid' : 'near';
        set('result-head', `Yes, ${tomorrow ? 'tomorrow' : 'today'}. ${distance(a.distance_km)} from our shop.`);
        const when = s.when ? s.when.charAt(0).toLowerCase() + s.when.slice(1) : '';
        const by = s.order_by ? `, if you order by ${s.order_by}${s.order_by_tomorrow ? ' tomorrow' : ''}` : '';
        const free = a.free_over != null ? ` Free delivery over ₹${Number(a.free_over).toLocaleString('en-IN')}.` : '';
        set('result-text', `${when ? `Arrives ${when}${by}.` : ''}${free}`.trim());
      }
    }

    // A returning customer with an address we deliver to has the answer in the header.
    if (this.hasAttribute('data-returning')) this.hidden = !!here && !(a && !a.serviceable);
  };
}

class CwHomeFavourites extends HTMLElement {
  connectedCallback() {
    this.row = this.querySelector('[data-cw="row"]');
    this.addEventListener('click', this.#onClick);
    this.row?.addEventListener('scroll', this.#arrows, { passive: true });
    this.#arrows();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.row?.removeEventListener('scroll', this.#arrows);
  }

  #onClick = (event) => {
    const arrow = /** @type {HTMLElement} */ (event.target).closest('[data-step]');
    if (!arrow || !this.row) return;
    this.row.scrollBy({ left: Number(arrow.dataset.step) * (this.row.clientWidth - 38), behavior: REDUCED.matches ? 'auto' : 'smooth' });
  };

  #arrows = () => {
    if (!this.row) return;
    const max = this.row.scrollWidth - this.row.clientWidth - 2;
    const [prev, next] = this.querySelectorAll('.cw-home-fav__arrow');
    if (prev) prev.disabled = this.row.scrollLeft <= 2;
    if (next) next.disabled = this.row.scrollLeft >= max;
  };
}

if (!customElements.get('cw-home-hero')) customElements.define('cw-home-hero', CwHomeHero);
if (!customElements.get('cw-home-delivery')) customElements.define('cw-home-delivery', CwHomeDelivery);
if (!customElements.get('cw-home-favourites')) customElements.define('cw-home-favourites', CwHomeFavourites);

/* ---------- Page: header room, folding on scroll, requests, logo ---------- */

const headerGroup = document.querySelector('.shopify-section-group-header-group');

/** Phones: the header is fixed; the page starts below its full (unfolded) height. */
function measureHeader() {
  const fold = document.querySelector('.cw-hdr-h__fold');
  const strip = document.querySelector('.cw-hdr-h__strip');
  if (!fold || !strip) return;
  const row = fold.firstElementChild;
  const rowHeight = row ? row.getBoundingClientRect().height : 0;
  const height = rowHeight + strip.getBoundingClientRect().height;
  if (height) document.body.style.setProperty('--cw-home-hdr', `${Math.round(height)}px`);
  // How far the header slides when it folds: exactly the logo row.
  if (rowHeight) document.body.style.setProperty('--cw-home-row', `${Math.round(rowHeight)}px`);
}

let folded = false;
let lastY = window.scrollY;
function fold(next) {
  if (next === folded) return;
  folded = next;
  document.dispatchEvent(new CustomEvent('cw:fold', { detail: { folded: next, navHidden: next } }));
}
function onScroll() {
  const y = window.scrollY;
  if (!PHONE.matches) {
    fold(false);
  } else if (y <= 4) {
    fold(false);
  } else if (y > lastY + 3 && y > 60) {
    fold(true);
  } else if (y < lastY - 3) {
    fold(false);
  }
  lastY = y;
}

// A screen over Home (Order Again) closed: Home shows its header and tabs again.
document.addEventListener('cw:fold:sync', () => fold(false));

if (headerGroup) {
  measureHeader();
  window.addEventListener('resize', measureHeader, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  PHONE.addEventListener?.('change', () => {
    measureHeader();
    fold(false);
  });
}

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);

  // The logo on Home: back to the top, not a reload.
  const top = target.closest('[data-cw="top"]');
  if (top) {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: REDUCED.matches ? 'auto' : 'smooth' });
    fold(false);
    return;
  }

  const button = target.closest('.cw-home-sec [data-open]');
  if (!button) return;
  const name = button.dataset.open;
  const request = new CustomEvent(`cw:${name}:open`, { cancelable: true, detail: { source: button } });
  document.dispatchEvent(request);
  if (!request.defaultPrevented && FALLBACK[name]) window.location.href = FALLBACK[name]();
});

// One shop video at a time.
document.addEventListener(
  'play',
  (event) => {
    const playing = event.target;
    if (!(playing instanceof HTMLVideoElement) || !playing.closest('.cw-home-shop')) return;
    for (const video of document.querySelectorAll('.cw-home-shop video')) if (video !== playing) video.pause();
  },
  true
);
